// FX Volatility Alert Banner — shows a red bar at the top of every admin page
// when the BCV / Paralelo exchange rate has drifted past the per-tenant
// threshold (default 5%) since the previous sync.
//
// Polls /api/fx-alerts/latest every 2 minutes while the admin is on any admin
// page. Dismiss button calls POST /api/fx-alerts/{id}/dismiss and immediately
// hides the banner.

import React, { useEffect, useState, useCallback } from "react";
import { tr } from '@/i18n/tr';
import { AlertTriangle, X, TrendingUp, TrendingDown, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { useCountry } from "@/context/CountryContext";

function fmtRate(v) {
  const n = Number(v || 0);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function providerLabel(p) {
  const key = String(p || "").toLowerCase();
  const test = key.includes("test") ? " (测试/Prueba)" : "";
  if (key.startsWith("paralelo")) return "Binance" + test;
  if (key.startsWith("bcv")) return "BCV" + test;
  return p;
}

export default function FxAlertBanner() {
  const { is_ve } = useCountry();
  const [alert, setAlert] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    if (is_ve === false) return; // Non-Venezuela tenants don't use BCV/paralelo rates
    try {
      const { data } = await axios.get(`${API}/fx-alerts/latest`);
      setAlert(data?.alert || null);
    } catch {
      // Silent — banner is opportunistic; do not disrupt the page on auth errors.
    }
  }, [is_ve]);

  useEffect(() => {
    if (is_ve === false) return;
    load();
    const t = setInterval(load, 120000); // 2-minute poll
    return () => clearInterval(t);
  }, [load, is_ve]);

  const dismiss = async () => {
    if (!alert?.id) return;
    setHidden(true);
    try {
      await axios.post(`${API}/fx-alerts/${alert.id}/dismiss`);
    } catch {
      // If dismiss fails, re-show so the boss retries rather than silently losing the alert
      setHidden(false);
    }
  };

  if (is_ve === false) return null; // Bs/BCV UI is Venezuela-only
  if (!alert || hidden) return null;
  const up = alert.direction === "up";
  const Icon = up ? TrendingUp : TrendingDown;
  const sign = alert.change_pct > 0 ? "+" : "";

  return (
    <div
      className="mb-4 rounded-lg border border-red-500/50 bg-gradient-to-r from-red-600/20 to-rose-600/10 px-4 py-3 flex items-start gap-3 text-sm shadow-lg shadow-red-900/20"
      data-testid="fx-alert-banner"
    >
      <div className="w-9 h-9 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center shrink-0">
        <AlertTriangle className="w-4 h-4 text-red-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-red-300">{tr('x_1c4d65a8')}</span>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${up ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid="fx-alert-direction">
            <Icon className="w-3 h-3" /> {sign}{Number(alert.change_pct).toFixed(2)}%
          </span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-slate-400 ml-auto">
            {providerLabel(alert.provider)}
          </span>
        </div>
        <p className="text-slate-200 mt-1 leading-relaxed">
          {providerLabel(alert.provider)}{" "}
          {up ? tr('x_943201f9') : tr('x_caecf5b4')}：
          <span className="text-slate-400 line-through mx-1">Bs. {fmtRate(alert.old_rate)}</span>
          →
          <span className="text-white font-bold ml-1" data-testid="fx-alert-new-rate">Bs. {fmtRate(alert.new_rate)}</span>
          <span className="text-slate-400 text-xs ml-2">{tr('x_9ba1a642')} {alert.threshold_pct}{tr('x_f876a49c')}</span>
        </p>
        <div className="flex gap-3 mt-1.5 items-center text-xs">
          {alert.whatsapp_sent ? (
            <span className="text-emerald-400">{tr('x_2a9db7a6')}</span>
          ) : (
            <span className="text-slate-500">{tr('x_b2a5c20e')}</span>
          )}
          <Link to="/admin/fx-alerts" className="text-red-300 hover:text-red-200 underline underline-offset-2 inline-flex items-center gap-1">
            <Settings className="w-3 h-3" /> {tr('x_f1a0be3e')}
          </Link>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="shrink-0 w-7 h-7 rounded-md bg-slate-700/50 hover:bg-slate-700 text-slate-300 hover:text-white transition flex items-center justify-center"
        title={tr('x_f345932a')}
        data-testid="fx-alert-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
